import { useRouter } from 'next/router';

import { Checkbox, FieldSet, FormHelp, Label, LabeledControl, Legend } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';

type GenderPreferences = {
  hasNoRoommateGenderPreference: boolean;
  hasRoommateGenderPreferenceFemale: boolean;
  hasRoommateGenderPreferenceMale: boolean;
  hasRoommateGenderPreferenceNonBinary: boolean;
};

const ROOMMATE_GENDER_PREFERENCE = {
  NO_PREFERENCE: 'noPreference',
  FEMALE: 'female',
  MALE: 'male',
  NON_BINARY: 'nonBinary',
} as const;

const GenderPreferenceFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const roommateGenderPreferences = listingDetails?.roomForRent?.roommateRequirements?.roommateGenderPreferences ?? [];

  const updateMutation = useMultiEditMutation();

  const genderPreferencesField = useFieldSave({
    fieldName: 'genderPreferences',
    initialValue: {
      hasNoRoommateGenderPreference: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.NO_PREFERENCE),
      hasRoommateGenderPreferenceFemale: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.FEMALE),
      hasRoommateGenderPreferenceMale: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.MALE),
      hasRoommateGenderPreferenceNonBinary: roommateGenderPreferences.includes(ROOMMATE_GENDER_PREFERENCE.NON_BINARY),
    },
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        property: {
          listingAlias,
          roomForRent: {
            hasNoRoommateGenderPreference: value.hasNoRoommateGenderPreference,
            hasRoommateGenderPreferenceFemale: value.hasRoommateGenderPreferenceFemale,
            hasRoommateGenderPreferenceMale: value.hasRoommateGenderPreferenceMale,
            hasRoommateGenderPreferenceNonBinary: value.hasRoommateGenderPreferenceNonBinary,
          },
        },
      },
    }),
    autoSaveOnChange: true,
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const key = name as keyof GenderPreferences;
    const value = checked;

    let newPreferences = {
      ...genderPreferencesField.value,
      [key]: value,
    };

    // Handle "No preference" mutual exclusivity
    if (key === 'hasNoRoommateGenderPreference') {
      if (newPreferences.hasNoRoommateGenderPreference) {
        // Setting "No preference" to true, clear all others
        newPreferences = {
          hasNoRoommateGenderPreference: true,
          hasRoommateGenderPreferenceFemale: false,
          hasRoommateGenderPreferenceMale: false,
          hasRoommateGenderPreferenceNonBinary: false,
        };
      } else {
        // Unchecking "No preference" - do nothing (will be caught by validation)
        newPreferences.hasNoRoommateGenderPreference = false;
      }
    } else {
      // If any specific preference is selected, clear "No preference"
      if (newPreferences[key]) {
        newPreferences.hasNoRoommateGenderPreference = false;
      }

      // If all specific preferences are false, default to "No preference"
      const hasAnySpecificPreference =
        newPreferences.hasRoommateGenderPreferenceFemale ||
        newPreferences.hasRoommateGenderPreferenceMale ||
        newPreferences.hasRoommateGenderPreferenceNonBinary;
      if (!hasAnySpecificPreference) {
        newPreferences.hasNoRoommateGenderPreference = true;
      }
    }

    genderPreferencesField.onChange(newPreferences);
  };

  const error = genderPreferencesField.error;

  return (
    <FieldSet
      data-testid="GenderPreferenceFormField"
      legend={<Legend>Gender preference</Legend>}
      error={!!error}
      description={<FormHelp>{error}</FormHelp>}
    >
      <LabeledControl
        label={<Label>No preference</Label>}
        control={
          <Checkbox
            name="hasNoRoommateGenderPreference"
            checked={genderPreferencesField.value.hasNoRoommateGenderPreference}
            onChange={handleCheckboxChange}
          />
        }
      />

      <LabeledControl
        label={<Label>Female</Label>}
        control={
          <Checkbox
            name="hasRoommateGenderPreferenceFemale"
            checked={genderPreferencesField.value.hasRoommateGenderPreferenceFemale}
            onChange={handleCheckboxChange}
          />
        }
      />

      <LabeledControl
        label={<Label>Male</Label>}
        control={
          <Checkbox
            name="hasRoommateGenderPreferenceMale"
            checked={genderPreferencesField.value.hasRoommateGenderPreferenceMale}
            onChange={handleCheckboxChange}
          />
        }
      />

      <LabeledControl
        label={<Label>Non-binary</Label>}
        control={
          <Checkbox
            name="hasRoommateGenderPreferenceNonBinary"
            checked={genderPreferencesField.value.hasRoommateGenderPreferenceNonBinary}
            onChange={handleCheckboxChange}
          />
        }
      />
    </FieldSet>
  );
};

export default GenderPreferenceFormField;
